import type React from 'react';
import { FILTER_MENU_TYPES } from '@components/filter-checkbox-selector/constants.ts';
import type { SortValue } from '@components/table';
import type { Filter } from '@types';

import type { FilterSearchPropsMenu } from '.';

export const getUpdatedRequestValue = (key: string, currentData: any, updateValue: any) => ({
    ...currentData,
    [key]: updateValue,
});

export const getFiltersFromLeaves = (leaves: SelectedLeafInfo[]) =>
    leaves.map(
        (leave) =>
            ({
                filter: leave.parent,
                value: leave.child,
            }) as Filter,
    );

export const applyFiltersToMenu = (
    menu: FilterSearchPropsMenu[],
    filters: Record<string, string[]> | null | undefined,
): FilterSearchPropsMenu[] => {
    if (!filters) {
        return menu;
    }

    return menu.map((group) => {
        const selectedValues = new Set(filters[group.name] ?? []);
        const submenu = group.submenu.map((item) => ({
            ...item,
            isSelected: selectedValues.has(item.name),
        }));

        return {
            ...group,
            submenu,
            isSelected: submenu.length > 0 && submenu.every((item) => item.isSelected),
        };
    });
};

export const toggleItemAtPath = (
    m: FilterSearchPropsMenu[],
    path: number[],
    checked: boolean,
): FilterSearchPropsMenu[] => {
    return m.map((item, index) => {
        if (index === path[0]) {
            if (path.length === 1) {
                return { ...item, isSelected: checked };
            }

            return {
                ...item,
                submenu: toggleItemAtPath(item.submenu, path.slice(1), checked),
            };
        }
        return item;
    });
};

export const updateMenuByPath = (
    menu: FilterSearchPropsMenu[],
    pathOrPaths: number[] | number[][],
    checked: boolean,
): FilterSearchPropsMenu[] => {
    const isSelectAllOperation = Array.isArray(pathOrPaths) && Array.isArray((pathOrPaths as any)[0]);

    if (isSelectAllOperation) {
        const paths = pathOrPaths as number[][];

        return paths.reduce<FilterSearchPropsMenu[]>((acc, p) => toggleItemAtPath(acc, p, checked), menu);
    }

    return toggleItemAtPath(menu, pathOrPaths as number[], checked);
};

export const getSubmenuByPath = (data: FilterSearchPropsMenu[], path: number[]): FilterSearchPropsMenu[] =>
    path.reduce((acc, idx) => acc[idx].submenu, data);

export const getSubmenuTypeByPath = (
    data: FilterSearchPropsMenu[],
    path: number[],
): FilterSearchPropsMenu['menuType'] | undefined => {
    const topIndex = path?.[0];
    if (topIndex === undefined) return undefined;

    return data[topIndex]?.menuType || FILTER_MENU_TYPES.Checkbox;
};

export interface SelectedLeafInfo {
    path: number[];
    parent: string | null;
    child: string;
}

export const getSelectedLeaves = (
    menu: FilterSearchPropsMenu[],
    path: number[] = [],
    parentName: string | null = null,
): SelectedLeafInfo[] => {
    let result: SelectedLeafInfo[] = [];

    menu.forEach((item, idx) => {
        const currentPath = [...path, idx];
        if (item.submenu.length === 0 && item.isSelected) {
            result.push({
                path: currentPath,
                parent: parentName,
                child: item.name,
            });
        }
        if (item.submenu.length > 0) {
            result = result.concat(getSelectedLeaves(item.submenu, currentPath, item.name));
        }
    });

    return result;
};

interface CreateHandlersOptions<RequestData> {
    dispatch: any;
    requestData: RequestData;
    updateMethod: (data: RequestData) => any;
    menu: FilterSearchPropsMenu[];
    setMenu: React.Dispatch<React.SetStateAction<FilterSearchPropsMenu[]>>;
    setClearAll: React.Dispatch<React.SetStateAction<boolean>>;
    getUpdatedFilters?: (selectedLeaves: SelectedLeafInfo[]) => Partial<RequestData>;
    defaultMenu?: FilterSearchPropsMenu[];
    supportsSort?: boolean;
    supportsPagination?: boolean;
}

export const getNormalizedFilters = (selectedFilters: SelectedLeafInfo[]): Record<string, string[]> =>
    selectedFilters.reduce<Record<string, string[]>>((acc, filter) => {
        if (!filter.parent || filter.child === '') return acc;
        (acc[filter.parent] ??= []).push(filter.child);

        return acc;
    }, {});

export const createGenericHandlers = <RequestData>({
    dispatch,
    requestData,
    updateMethod,
    menu,
    setMenu,
    setClearAll,
    getUpdatedFilters,
    defaultMenu = [],
    supportsSort = true,
    supportsPagination = true,
}: CreateHandlersOptions<RequestData>) => {
    const onSearchTermChange = (value: string) => {
        dispatch(updateMethod(getUpdatedRequestValue('textSearch', requestData, value)));
    };

    // overriddenMenu param - necessary for the radio menu type to work properly without visual glitches
    // and redundantly calling dispatch action when previously chosen element is deselected
    // it just comes without any items selected so the chosen radio option update is done here once
    const onCheckboxSelected = (
        path: number[] | number[][],
        checked: boolean,
        overriddenMenu?: FilterSearchPropsMenu[],
    ) => {
        const updatedMenu = updateMenuByPath(overriddenMenu || menu, path, checked);
        const selectedLeaves = getSelectedLeaves(updatedMenu);

        const updated = getUpdatedFilters
            ? getUpdatedFilters(selectedLeaves)
            : (getUpdatedRequestValue(
                  'filters',
                  requestData,
                  getFiltersFromLeaves(selectedLeaves),
              ) as Partial<RequestData>);

        dispatch(updateMethod({ ...requestData, ...updated }));
        setMenu(updatedMenu);
    };

    const onClearAllFilters = () => {
        setMenu(defaultMenu);
        setClearAll((prev) => !prev);
        dispatch(
            updateMethod({
                ...requestData,
                textSearch: '',
                ...(getUpdatedFilters ? getUpdatedFilters([]) : { filters: [] }),
            }),
        );
    };

    const onClearSearch = () => {
        dispatch(updateMethod({ ...requestData, textSearch: '' }));
    };

    const onSortChange = supportsSort
        ? (sort: SortValue) => {
              dispatch(
                  updateMethod({
                      ...requestData,
                      columnSort: sort.columnId,
                      sortOrder: sort.order === 'desc' ? 'descending' : 'ascending',
                  }),
              );
          }
        : undefined;

    const onPaginationChange = supportsPagination
        ? (pageNumber: number, pageSize: number) => {
              dispatch(
                  updateMethod({
                      ...requestData,
                      pagination: {
                          pageNumber,
                          pageSize,
                      },
                  }),
              );
          }
        : undefined;

    const getActiveFilters = (filters: { filter: string; value: string }[]): string[] =>
        filters.map(({ filter }) => (filter.toLowerCase().includes('title') ? 'title' : filter.toLowerCase()));

    return {
        onSearchTermChange,
        onCheckboxSelected,
        onClearAllFilters,
        onClearSearch,
        onSortChange,
        onPaginationChange,
        getActiveFilters,
    };
};
